import { Link } from 'gatsby';
import { GatsbyImage, GatsbyImageProps, ImageDataLike, StaticImage, getImage } from 'gatsby-plugin-image';
import { BynderImageData, ContentfulAsset } from 'graphqlTypes';
import styled, { DefaultTheme, ThemedStyledFunction } from 'styled-components';

import fieldRouteBanner from 'assets/images/banner.jpeg';

import { BasicProps, basic } from 'quarks/interpolations/basic';

import type { FC } from 'react';

type StaticImage = {
  file: {
    url: string;
  };
};

const StyledImage = styled.img`
  ${basic}
`;

export type ImageData = ImageDataLike | BynderImageData | StaticImage;

const isStaticImage = (image: ImageData): image is StaticImage => (image as StaticImage).file?.url !== undefined;

const isBynderImage = (image: ImageData): image is BynderImageData =>
  (image as BynderImageData)?.[0]?.thumbnails?.src !== undefined;

const imageResolver = (image: ImageData) => {
  const gatsbyImageData = 'gatsbyImageData' in image && image.gatsbyImageData ? getImage(image) : undefined;

  const src = (() => {
    if (isStaticImage(image)) {
      return image.file?.url;
    }
    if (isBynderImage(image)) {
      return image[0]?.thumbnails?.src;
    }

    return undefined;
  })();

  return {
    as: gatsbyImageData ? GatsbyImage : ('img' as const),
    image: gatsbyImageData,
    src,
  };
};

type SomeGatsbyImageProps = Omit<GatsbyImageProps, 'objectFit' | 'objectPosition' | 'backgroundColor' | 'image' | 'as'>;

type StyledImageProps = ThemedStyledFunction<'img', DefaultTheme, BasicProps, never>;

export interface ImageProps extends BasicProps, Partial<StyledImageProps>, SomeGatsbyImageProps {
  image: ImageData | ContentfulAsset;
  alt: string;
  white?: boolean;
  link?: string;
  roundedCorners?: boolean;
}

/**
 * Conditionally renders a GatsbyImage if possible or a <img> tag if not.
 */
const Image: FC<ImageProps> = ({ image, alt, boxShadow, roundedCorners, white, link, ...props }) => {
  if (!image) {
    console.warn(`Image with alt: ${alt} is ${image}`);

    return <StyledImage src={fieldRouteBanner} alt="Field Routes Default Banner Image" />;
  }

  const ImageToRender = (imageProps: ImageProps) => (
    <>
      {link ? (
        <Link to={link}>
          <StyledImage {...imageProps} />
        </Link>
      ) : (
        <StyledImage {...imageProps} />
      )}
    </>
  );

  return (
    // @ts-expect-error StyledComponents' definition of onLoad conflicts with GatsbyImage's
    <ImageToRender
      {...imageResolver(image as ImageData)}
      title=""
      alt={alt}
      {...props}
      filter={image && white ? 'brightness(0) invert(1)' : undefined}
      boxShadow={boxShadow ? 'lg' : null}
      {...(roundedCorners && { borderRadius: '20px' })}
    />
  );
};

export default Image;
